import React, { useEffect, useRef } from 'react';
import MaskedInput from 'react-text-mask';
import LoadingButton from './common/LoadingButton';
import Card from './common/Card';

const phoneNumberMask = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/
];

const GeneralInquiryForm: React.FC = () => {
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<any>(null);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [organization, setOrganization] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  const [inquiryType, setInquiryType] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [submitState, setSubmitState] = React.useState<
    'IDLE' | 'SUBMITTING' | 'SUCCESS' | 'ERROR'
  >('IDLE');

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(emailValue)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePhone = (phoneValue: string): boolean => {
    // Remove all non-digit characters for validation
    const digitsOnly = phoneValue.replace(/\D/g, '');
    // If phone is provided, it must be exactly 10 digits
    if (phoneValue && digitsOnly.length !== 10) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    setPhone(value);
    if (phoneError) {
      validatePhone(value);
    }
  };

  useEffect(() => {
    // Load Cloudflare Turnstile script with explicit rendering
    const existing = document.querySelector(
      'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
    ) as HTMLScriptElement | null;

    const renderWidget = () => {
      // @ts-ignore - turnstile is provided by the script
      if (typeof turnstile !== 'undefined' && turnstileContainerRef.current) {
        try {
          // @ts-ignore
          const id = turnstile.render(turnstileContainerRef.current, {
            sitekey: process.env.GATSBY_TURNSTILE_SITE_KEY as string,
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            'error-callback': () => {
              setTurnstileToken(null);
            },
            'expired-callback': () => {
              setTurnstileToken(null);
            }
          });
          turnstileWidgetIdRef.current = id;
        } catch {}
      }
    };

    if (existing) {
      // If script already present
      // @ts-ignore
      if (typeof turnstile !== 'undefined') {
        renderWidget();
      } else {
        existing.addEventListener('load', renderWidget);
      }
    } else {
      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.defer = true; // Do not use turnstile.ready(); render on load instead
      script.addEventListener('load', renderWidget);
      document.body.appendChild(script);
    }

    return () => {
      // Best-effort cleanup: reset token and remove widget if possible
      setTurnstileToken(null);
      // @ts-ignore
      if (typeof turnstile !== 'undefined' && turnstileWidgetIdRef.current) {
        try {
          // @ts-ignore
          turnstile.remove(turnstileWidgetIdRef.current);
        } catch {}
      }
      turnstileWidgetIdRef.current = null;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email and phone
    const isEmailValid = validateEmail(email);
    const isPhoneValid = validatePhone(phone);
    
    if (!firstName || !lastName || !isEmailValid || !subject || !message || !isPhoneValid) {
      return;
    }

    // Check if Turnstile was completed
    if (!turnstileToken) {
      setSubmitState('ERROR');
      return;
    }

    try {
      setSubmitState('SUBMITTING');
      const response = await fetch(
        `${process.env.GATSBY_TCAL_API_URL}/contact`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            phone: phone || undefined,
            organization: organization || undefined,
            inquiryType: inquiryType || undefined,
            subject,
            message,
            turnstileToken
          })
        }
      );
      if (!response.ok) {
        setSubmitState('ERROR');
        return;
      }
      setSubmitState('SUCCESS');
      setFirstName('');
      setLastName('');
      setEmail('');
      setEmailError('');
      setSubject('');
      setOrganization('');
      setPhone('');
      setPhoneError('');
      setInquiryType('');
      setMessage('');
      setTurnstileToken(null);
      // Reset Turnstile widget
      // @ts-ignore
      if (typeof turnstile !== 'undefined' && turnstileWidgetIdRef.current) {
        try {
          // @ts-ignore
          turnstile.reset(turnstileWidgetIdRef.current);
        } catch {}
      }
    } catch {
      setSubmitState('ERROR');
    }
  };

  return (
    <div className="max-w-3xl mx-auto text-left">
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold sans-serif">
            General Inquiries
          </h2>
          <p className="text-gray-600 mt-2 sans-serif">
            Use this form for questions not related to professional development
            services.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
                First name
              </label>
              <input
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                placeholder="Jane"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
                Last name
              </label>
              <input
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
                Email
              </label>
              <input
                type="email"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none sans-serif ${
                  emailError
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-primary'
                }`}
                value={email}
                onChange={handleEmailChange}
                onBlur={() => validateEmail(email)}
                required
                placeholder="jane@example.com"
              />
              {emailError && (
                <p className="text-red-600 text-sm mt-1 sans-serif">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
                Organization (optional)
              </label>
              <input
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
                value={organization}
                onChange={e => setOrganization(e.target.value)}
                placeholder="School or district"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
                Phone (optional)
              </label>
              <MaskedInput
                mask={phoneNumberMask}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none sans-serif ${
                  phoneError
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-primary'
                }`}
                value={phone}
                onChange={handlePhoneChange}
                onBlur={() => validatePhone(phone)}
                placeholder="(555) 555-5555"
                guide={false}
              />
              {phoneError && (
                <p className="text-red-600 text-sm mt-1 sans-serif">
                  {phoneError}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
              Inquiry type
            </label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
              value={inquiryType}
              onChange={e => setInquiryType(e.target.value)}
            >
              <option value="">Select an option</option>
              <option value="Certificates">Certificates</option>
              <option value="Courses">Courses</option>
              <option value="Events">Events</option>
              <option value="Purchasing">Purchasing</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
              Subject
            </label>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              required
              placeholder="How can we help?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 sans-serif mb-1">
              Message
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none sans-serif"
              rows={6}
              value={message}
              onChange={e => setMessage(e.target.value)}
              required
              placeholder="Share a few details so we can best assist you."
            />
          </div>

          <div className="mt-6 mb-4">
            <div ref={turnstileContainerRef} id="turnstile-widget" />
          </div>

          <div className="pt-2">
            <LoadingButton
              type="submit"
              text="Submit"
              size="md"
              isLoading={submitState === 'SUBMITTING'}
              disabled={!turnstileToken}
            />
          </div>

          {submitState === 'SUCCESS' && (
            <div className="p-4 bg-green-50 border border-green-300 rounded-md text-green-800 sans-serif">
              Thanks! We've received your message and will get back to you soon.
            </div>
          )}
          {submitState === 'ERROR' && (
            <div className="p-4 bg-red-50 border border-red-300 rounded-md text-red-800 sans-serif">
              Sorry, something went wrong. Please try again later.
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default GeneralInquiryForm;
